import { Link, useParams, useSearch } from "@tanstack/react-router";
import { ArrowLeft, KeyRound, MonitorSmartphone, PackageCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  useClientLicensingQuery,
	useAuthorityCustomerQuery,
	useTenantSessionQuery,
  useRevokeClientProductEntitlementMutation,
  useSuspendClientProductEntitlementMutation
} from "@/api/query-hooks";
import type { ClientProductEntitlementRecord } from "@/api/types";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ProductActivationTable } from "@/features/licensing/product-activation-table";
import { MonitoringTargetsPanel } from "@/features/licensing/monitoring-targets-panel";
import { LocalCustomerActions } from "@/features/licensing/local-customer-actions";
import { formatDateTime } from "@/lib/date";

export function ClientLicensingPage() {
  const { clientId: rawClientId } = useParams({ from: "/clients/$clientId" });
  const source = useSearch({ from: "/clients/$clientId" });
  const parsedClientId = /^[1-9]\d*$/.test(rawClientId) ? Number(rawClientId) : Number.NaN;
  const clientId = Number.isSafeInteger(parsedClientId) ? parsedClientId : Number.NaN;
  const { t, i18n } = useTranslation(["licensing", "common"]);
  const query = useClientLicensingQuery(clientId);
	const clientQuery = useAuthorityCustomerQuery(clientId);
  const session = useTenantSessionQuery();
  const suspend = useSuspendClientProductEntitlementMutation();
  const revoke = useRevokeClientProductEntitlementMutation();

  const backLink = <DirectoryBackLink q={source.fromQ} clientId={source.fromClientId} view={source.fromView} label={t("directory.back")} />;
  if (!Number.isSafeInteger(clientId)) {
    return <div className="grid gap-5">{backLink}<EmptyState title={t("client.notFound")} /></div>;
  }
  if (query.isLoading) return <div className="grid gap-5">{backLink}<LoadingState /><MonitoringTargetsPanel clientId={clientId} clientName={clientQuery.data?.name} /></div>;
  if (query.isError) return <div className="grid gap-5">{backLink}<ErrorState title={t("common:states.error")} detail={query.error.message} onRetry={() => void query.refetch()} /><MonitoringTargetsPanel clientId={clientId} clientName={clientQuery.data?.name} /></div>;
  if (!query.data) return <div className="grid gap-5">{backLink}<EmptyState title={t("client.notFound")} /><MonitoringTargetsPanel clientId={clientId} clientName={clientQuery.data?.name} /></div>;
  const data = query.data;

  const mutateEntitlement = (entitlement: ClientProductEntitlementRecord, action: "suspend" | "revoke") => {
    const message = action === "suspend" ? t("client.confirmSuspend", { product: entitlement.appId }) : t("client.confirmRevoke", { product: entitlement.appId });
    if (!window.confirm(message)) return;
    const mutation = action === "suspend" ? suspend : revoke;
    mutation.mutate(
      { clientId, appId: entitlement.appId, expectedRevision: entitlement.revision },
      {
        onSuccess: () => toast.success(action === "suspend" ? t("client.suspended") : t("client.revoked")),
        onError: (error) => toast.error(error instanceof Error ? error.message : t("common:states.error"))
      }
    );
  };

  return (
    <div className="grid gap-5">
      {backLink}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold"><KeyRound className="h-5 w-5" />{clientQuery.data?.name ?? t("client.title", { clientId })}</h2>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
			<span>{t("directory.customerNumber", { number: clientQuery.data?.number ?? clientId })}</span>
			<span>{t("directory.clientId", { clientId })}</span>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("client.description")}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {clientQuery.data ? <Badge variant={statusVariant(clientQuery.data.status)}>{clientQuery.data.status}</Badge> : null}
          <Badge variant={statusVariant(data.clientStatus?.status)}>{t("client.status", { status: data.clientStatus?.status ?? t("common:states.unknown") })}</Badge>
          {session.data?.canManageCustomers && clientQuery.data ? <LocalCustomerActions customer={clientQuery.data} /> : null}
        </div>
      </div>

      <nav className="sticky top-16 z-10 flex max-w-full gap-2 overflow-x-auto rounded-md border bg-panel/95 p-2 shadow-sm backdrop-blur" aria-label={t("client.sectionNavigation")}>
        {[
          ["products", t("client.products")],
          ["pending", t("client.pending")],
          ["devices", t("client.devices")],
          ["stations", t("client.stations")],
          ["monitoring-targets", t("targets.title")]
        ].map(([id, label]) => (
          <a key={id} href={`#${id}`} className="shrink-0 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {label}
          </a>
        ))}
      </nav>

      <section id="products" className="scroll-mt-32 overflow-hidden rounded-md border bg-panel">
        <SectionTitle icon={<PackageCheck className="h-4 w-4" />} title={t("client.products")} />
        {data.entitlements.length === 0 ? <InlineEmpty text={t("client.noProducts")} /> : (
          <div className="overflow-x-auto max-md:overflow-visible"><table className="w-full text-sm max-md:block">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground max-md:hidden"><tr>
              <th className="px-3 py-2">{t("columns.product")}</th><th className="px-3 py-2">{t("columns.status")}</th>
              <th className="px-3 py-2">{t("columns.validUntil")}</th><th className="px-3 py-2">{t("columns.updated")}</th>
              <th className="px-3 py-2 text-right">{t("columns.actions")}</th>
            </tr></thead>
            <tbody className="max-md:block max-md:space-y-3 max-md:p-3">{data.entitlements.map((item) => <tr key={item.appId} data-testid="client-entitlement-item" className="border-t max-md:block max-md:rounded-md max-md:border max-md:p-3">
              <td className="px-3 py-2 font-medium max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.product")}</MobileLabel>{item.appId}</td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.status")}</MobileLabel><Badge variant={statusVariant(item.status)}>{item.status}</Badge></td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.validUntil")}</MobileLabel>{formatDateTime(item.validUntil, i18n.language)}</td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.updated")}</MobileLabel>{formatDateTime(item.updatedAt, i18n.language)}</td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:pt-2"><MobileLabel>{t("columns.actions")}</MobileLabel><div className="flex flex-wrap justify-end gap-2 max-md:justify-start">
                {item.status === "ACTIVE" ? <Button size="sm" variant="outline" onClick={() => mutateEntitlement(item, "suspend")}>{t("client.suspend")}</Button> : null}
                {item.status !== "REVOKED" ? <Button size="sm" variant="destructive" onClick={() => mutateEntitlement(item, "revoke")}>{t("client.revoke")}</Button> : null}
              </div></td>
            </tr>)}</tbody>
          </table></div>
        )}
      </section>

      <section id="pending" className="scroll-mt-32 overflow-hidden rounded-md border bg-panel">
        <SectionTitle icon={<MonitorSmartphone className="h-4 w-4" />} title={t("client.pending")} />
        {data.pendingActivations.length === 0 ? <InlineEmpty text={t("client.noPending")} /> : <ProductActivationTable items={data.pendingActivations} showClient={false} />}
      </section>

      <section id="devices" className="scroll-mt-32 overflow-hidden rounded-md border bg-panel">
        <SectionTitle icon={<MonitorSmartphone className="h-4 w-4" />} title={t("client.devices")} />
        {data.devices.length === 0 ? <InlineEmpty text={t("client.noDevices")} /> : (
          <div className="overflow-x-auto max-md:overflow-visible"><table className="w-full text-sm max-md:block">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground max-md:hidden"><tr>
              <th className="px-3 py-2">{t("columns.device")}</th><th className="px-3 py-2">{t("columns.platform")}</th>
              <th className="px-3 py-2">{t("columns.status")}</th><th className="px-3 py-2">{t("columns.lastSeen")}</th>
            </tr></thead>
            <tbody className="max-md:block max-md:space-y-3 max-md:p-3">{data.devices.map((item) => <tr key={item.deviceId} data-testid="client-device-item" className="border-t max-md:block max-md:rounded-md max-md:border max-md:p-3">
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.device")}</MobileLabel><div className="font-medium">{item.hostname || t("activation.unnamedDevice")}</div><div className="break-all font-mono text-xs text-muted-foreground">{item.deviceId}</div></td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.platform")}</MobileLabel>{item.os} / {item.arch}</td><td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.status")}</MobileLabel><Badge variant={statusVariant(item.status)}>{item.status}</Badge></td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.lastSeen")}</MobileLabel>{formatDateTime(item.lastSeenAt, i18n.language)}</td>
            </tr>)}</tbody>
          </table></div>
        )}
      </section>

      <section id="stations" className="scroll-mt-32 overflow-hidden rounded-md border bg-panel">
        <SectionTitle icon={<PackageCheck className="h-4 w-4" />} title={t("client.stations")} />
        {data.stations.length === 0 ? <InlineEmpty text={t("client.noStations")} /> : (
          <div className="overflow-x-auto max-md:overflow-visible"><table className="w-full text-sm max-md:block">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground max-md:hidden"><tr>
              <th className="px-3 py-2">{t("columns.product")}</th><th className="px-3 py-2">{t("columns.device")}</th>
              <th className="px-3 py-2">{t("columns.version")}</th><th className="px-3 py-2">{t("columns.status")}</th><th className="px-3 py-2">{t("columns.lastSeen")}</th>
            </tr></thead>
            <tbody className="max-md:block max-md:space-y-3 max-md:p-3">{data.stations.map((item) => <tr key={item.stationId} data-testid="client-station-item" className="border-t max-md:block max-md:rounded-md max-md:border max-md:p-3">
              <td className="px-3 py-2 font-medium max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.product")}</MobileLabel>{item.appId}</td><td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.device")}</MobileLabel>{item.hostname || item.deviceId}<div className="text-xs text-muted-foreground">{item.os} / {item.arch}</div></td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.version")}</MobileLabel>{item.currentVersion}<div className="text-xs text-muted-foreground">{item.releaseChannel}</div></td>
              <td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.status")}</MobileLabel><Badge variant={statusVariant(item.status)}>{item.status}</Badge></td><td className="px-3 py-2 max-md:block max-md:px-0 max-md:py-1"><MobileLabel>{t("columns.lastSeen")}</MobileLabel>{formatDateTime(item.lastSeenAt, i18n.language)}</td>
            </tr>)}</tbody>
          </table></div>
        )}
      </section>

      <MonitoringTargetsPanel clientId={clientId} clientName={clientQuery.data?.name} />
    </div>
  );
}

function DirectoryBackLink({ q, clientId, view, label }: { q?: string; clientId?: string; view?: "all"; label: string }) {
  return (
    <div>
      <Link
        to="/clients"
        search={{ q, anchor: clientId, view }}
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />{label}
      </Link>
    </div>
  );
}

function SectionTitle({ icon, title }: { icon: ReactNode; title: string }) {
  return <div className="flex items-center gap-2 border-b px-4 py-3 font-medium">{icon}{title}</div>;
}

function InlineEmpty({ text }: { text: string }) {
  return <div className="px-4 py-8 text-center text-sm text-muted-foreground">{text}</div>;
}

function MobileLabel({ children }: { children: ReactNode }) {
  return <span className="mb-1 block text-xs font-normal uppercase text-muted-foreground md:hidden">{children}</span>;
}

function statusVariant(status?: string): "success" | "warning" | "destructive" | "default" {
  const normalized = status?.toUpperCase();
  if (normalized === "ACTIVE" || normalized === "HEALTHY" || normalized === "OK") return "success";
  if (normalized === "SUSPENDED" || normalized === "WARNING") return "warning";
  if (normalized === "REVOKED" || normalized === "DECOMMISSIONED" || normalized === "BLOCKED") return "destructive";
  return "default";
}
